import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons'
import { Flex, Tooltip } from '@mantine/core'
import { useMemo } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct } from '@flecto/types'
import {
  DataTable,
  Icon,
  TextCurrency,
  type DataTableColumn,
  type DataTableProps,
} from '@flecto/ui'

import { CellGroup } from '../TableBookingProductsSelection/CellGroup'
import { TextBookingProductPricingRule } from '../TextBookingProductPricingRule/TextBookingProductPricingRule'
import { CellName } from './CellName'

export type TableBookingProductsReadOnlyProps = Omit<
  DataTableProps<CoreBookingProduct[]>,
  'columns'
> & {
  bookingProducts?: CoreBookingProduct[]
  withTaxesPrices?: boolean
}

export function TableBookingProductsReadOnly({
  bookingProducts,
  withTaxesPrices,
  ...props
}: TableBookingProductsReadOnlyProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })

  const columns: DataTableColumn<CoreBookingProduct>[] = [
    {
      accessor: 'name',
      title: t('label_product'),
      width: 250,
      cellsStyle: () => ({ border: 0 }),
      render: (row) =>
        !row.bundle ? (
          <CellName bookingProduct={row} />
        ) : (
          <CellGroup category={`${row.id}`} />
        ),
    },
    {
      accessor: 'quantity',
      title: t('label_quantity'),
      width: 100,
      textAlign: 'center',
      cellsStyle: () => ({ border: 0 }),
      render: (row) => !row.bundle && row.quantity,
    },
    {
      accessor: 'item_price',
      width: 'fit-content',
      title: t('label_price_per_item_tiers'),
      cellsStyle: () => ({ border: 0 }),
      render: (row) => {
        if (!row.bundle) {
          return (
            <Flex align='center' gap='xs' wrap='nowrap'>
              <TextBookingProductPricingRule
                bookingProduct={row}
                withPrice
                withTax={withTaxesPrices}
                style={{ whiteSpace: 'nowrap' }}
              />
            </Flex>
          )
        }
      },
    },
    {
      accessor: 'rental_price',
      width: 'fit-content',
      cellsStyle: () => ({ border: 0 }),
      title: (
        <Tooltip
          label={
            withTaxesPrices
              ? t('label_including_taxes')
              : t('label_excluding_taxes')
          }
          withinPortal
        >
          <Flex align='center' gap='xs' style={{ cursor: 'default' }}>
            {t('label_price_rental')}
            <Icon icon={faQuestionCircle} size='xs' />
          </Flex>
        </Tooltip>
      ),
      render: (row) => {
        if (!row.bundle) {
          return (
            <TextCurrency
              cents={
                withTaxesPrices
                  ? (row?.price_with_vat_cents ?? 0)
                  : (row?.pricing_tier?.price_cents ?? 0)
              }
            />
          )
        }
      },
    },
    {
      accessor: 'deposit_price',
      title: t('label_price_deposit'),
      width: 120,
      cellsStyle: () => ({ border: 0 }),
      render: (row) => {
        if (!row.bundle) {
          return (row.deposit_cents ?? 0) <= 0 ? (
            '-'
          ) : (
            <TextCurrency cents={row.deposit_cents} />
          )
        }
      },
    },
  ]

  const groupedInquiryProducts = useMemo(() => {
    return (bookingProducts ?? [])
      .map((bookingProduct) => {
        const categoryBreadcrumb =
          bookingProduct?.product?.categories?.join(' > ') || ''

        return {
          ...bookingProduct,
          categoryName: categoryBreadcrumb,
          group: categoryBreadcrumb,
        }
      })
      .sort((itemA, itemB) =>
        itemA?.categoryName?.localeCompare(itemB?.categoryName)
      )
  }, [bookingProducts])

  return (
    // @ts-expect-error page prop can be undefined
    <DataTable {...props} records={groupedInquiryProducts} columns={columns} />
  )
}
